import {
  ISpeechAnswer,
  ISpeechWord
} from 'common/modules/ReactShared/data/api/lessonTask/types';
import { t } from 'i18n';
import { FC, useCallback, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

interface IProps {
  answer: ISpeechAnswer;
  correct: boolean;
  currentThreshold: number;
  thresholdDecay: number;
  studentAudioUrl?: string;
  teacherAudioUrl?: string;
}

const getScoreColor = (score: number): string => {
  if (score >= 80) return '#39B54A';
  if (score >= 60) return '#FFA726';
  return '#FF7678';
};

const getWordColor = (errorType: ISpeechWord['errorType']): string => {
  switch (errorType) {
    case 'None':
      return '#39B54A';
    case 'Mispronunciation':
      return '#FF7678';
    case 'Omission':
      return 'rgba(251, 252, 255, 0.4)';
    case 'Insertion':
      return '#FFA726';
    default:
      return 'rgba(251, 252, 255, 0.4)';
  }
};

const METRIC_KEYS = ['accuracyScore', 'fluencyScore', 'completenessScore'] as const;

const METRIC_LABELS: Record<
  (typeof METRIC_KEYS)[number],
  { key: string; defaultValue: string }
> = {
  accuracyScore: {
    key: 'frontend.lesson_task.tasks.play.dictation.speech.accuracy',
    defaultValue: 'Accuracy'
  },
  fluencyScore: {
    key: 'frontend.lesson_task.tasks.play.dictation.speech.fluency',
    defaultValue: 'Fluency'
  },
  completenessScore: {
    key: 'frontend.lesson_task.tasks.play.dictation.speech.completeness',
    defaultValue: 'Completeness'
  }
};

const CIRCLE_RADIUS = 24;
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS;

const MAIN_CIRCLE_RADIUS = 32;
const MAIN_CIRCLE_CIRCUMFERENCE = 2 * Math.PI * MAIN_CIRCLE_RADIUS;

interface ICircularScoreProps {
  score: number;
  color: string;
  size: number;
  radius: number;
  circumference: number;
  strokeWidth: number;
  fontSize: number;
}

const CircularScore: FC<ICircularScoreProps> = ({
  score,
  color,
  size,
  radius,
  circumference,
  strokeWidth,
  fontSize
}) => {
  const roundedScore = Math.round(score);
  const offset = circumference - (roundedScore / 100) * circumference;

  return (
    <SSvg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="rgba(255, 255, 255, 0.1)"
        strokeWidth={strokeWidth}
      />
      <SProgressCircle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        $offset={offset}
      />
      <text
        x={size / 2}
        y={size / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fill="#fff"
        fontWeight="700"
        fontSize={fontSize}
        transform={`rotate(90 ${size / 2} ${size / 2})`}
      >
        {roundedScore}
      </text>
    </SSvg>
  );
};

const CompactAudioPlayer: FC<{ src: string; label: string }> = ({ src, label }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);

  const toggle = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
    } else {
      el.currentTime = 0;
      el.play();
    }
    setPlaying(!playing);
  }, [playing]);

  const handleEnded = useCallback(() => setPlaying(false), []);

  const playLabel = playing
    ? t('frontend.lesson_task.tasks.play.dictation.speech.pause_audio', {
        defaultValue: 'Pause audio',
        label
      })
    : t('frontend.lesson_task.tasks.play.dictation.speech.play_audio', {
        defaultValue: 'Play audio',
        label
      });

  return (
    <SAudioRow>
      <audio ref={audioRef} src={src} onEnded={handleEnded} />
      <SAudioPlayButton
        type="button"
        onClick={toggle}
        $playing={playing}
        aria-label={playLabel}
      >
        {playing ? '⏸' : '▶'}
      </SAudioPlayButton>
      <SAudioLabel>{label}</SAudioLabel>
    </SAudioRow>
  );
};

const SpeechResultsFeedback: FC<IProps> = ({
  answer,
  correct,
  currentThreshold,
  thresholdDecay,
  studentAudioUrl,
  teacherAudioUrl
}) => {
  const nextThreshold = Math.max(0, currentThreshold - thresholdDecay);
  const mainColor = correct ? '#39B54A' : '#FF7678';

  return (
    <SWrapper>
      <SScoreRow>
        <CircularScore
          score={answer.pronunciationScore}
          color={mainColor}
          size={76}
          radius={MAIN_CIRCLE_RADIUS}
          circumference={MAIN_CIRCLE_CIRCUMFERENCE}
          strokeWidth={5}
          fontSize={22}
        />
        <SScoreInfo>
          <SScoreTitle>
            {correct
              ? t('frontend.lesson_task.tasks.play.dictation.speech.great_job', {
                  defaultValue: 'Great job!'
                })
              : t('frontend.lesson_task.tasks.play.dictation.speech.try_again', {
                  defaultValue: 'Try again'
                })}
          </SScoreTitle>
          {!correct && (
            <SThresholdHint>
              {t('frontend.lesson_task.tasks.play.dictation.speech.next_threshold', {
                defaultValue: 'Next threshold: {threshold}',
                threshold: nextThreshold
              })}
            </SThresholdHint>
          )}
        </SScoreInfo>
      </SScoreRow>

      <SWordsContainer>
        <SWordsList>
          {answer.words.map((word, index) => (
            <SWord
              key={`${word.word}-${index}`}
              $color={getWordColor(word.errorType)}
              $isOmitted={word.errorType === 'Omission'}
              $hasError={word.errorType !== 'None'}
            >
              {word.word}
            </SWord>
          ))}
        </SWordsList>
      </SWordsContainer>

      {(studentAudioUrl || teacherAudioUrl) && (
        <SAudioCompareContainer>
          <SAudioCompareTitle>
            {t('frontend.lesson_task.tasks.play.dictation.speech.compare_audio', {
              defaultValue: 'Compare audio'
            })}
          </SAudioCompareTitle>
          {studentAudioUrl && (
            <CompactAudioPlayer
              src={studentAudioUrl}
              label={t(
                'frontend.lesson_task.tasks.play.dictation.speech.your_recording',
                {
                  defaultValue: 'Your recording'
                }
              )}
            />
          )}
          {teacherAudioUrl && (
            <CompactAudioPlayer
              src={teacherAudioUrl}
              label={t('frontend.lesson_task.tasks.play.dictation.speech.teacher_audio', {
                defaultValue: "Teacher's audio"
              })}
            />
          )}
        </SAudioCompareContainer>
      )}

      <SMetricsRow>
        {METRIC_KEYS.map((key) => (
          <SMetric key={key}>
            <CircularScore
              score={answer[key]}
              color={getScoreColor(answer[key])}
              size={60}
              radius={CIRCLE_RADIUS}
              circumference={CIRCLE_CIRCUMFERENCE}
              strokeWidth={4}
              fontSize={17}
            />
            <SMetricLabel>
              {/* @tolgee-ignore */}
              {t(METRIC_LABELS[key].key, {
                defaultValue: METRIC_LABELS[key].defaultValue
              })}
            </SMetricLabel>
          </SMetric>
        ))}
      </SMetricsRow>
    </SWrapper>
  );
};

export default SpeechResultsFeedback;

const fillProgress = keyframes`
  from { stroke-dashoffset: var(--circumference); }
  to { stroke-dashoffset: var(--target-offset); }
`;

const SWrapper = styled.div`
  width: 100%;
`;

const SScoreRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const SSvg = styled.svg`
  transform: rotate(-90deg);
  flex-shrink: 0;
`;

const SProgressCircle = styled.circle<{ $offset: number }>`
  --circumference: ${({ strokeDasharray }) => strokeDasharray};
  --target-offset: ${({ $offset }) => $offset};
  stroke-dashoffset: ${({ $offset }) => $offset};
  animation: ${fillProgress} 0.6s ease-out;
`;

const SScoreInfo = styled.div``;

const SScoreTitle = styled.div`
  font-weight: 600;
  font-size: 1.5rem;
  color: #fbfcff;
`;

const SThresholdHint = styled.div`
  font-size: 0.875rem;
  color: rgba(251, 252, 255, 0.7);
  margin-top: 2px;
`;

const SWordsContainer = styled.div`
  background: rgba(122, 67, 136, 0.4);
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 12px;
`;

const SWordsList = styled.div`
  font-size: 1.25rem;
  line-height: 2;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  color: #fbfcff;
`;

const SWord = styled.span<{ $color: string; $isOmitted: boolean; $hasError: boolean }>`
  border-bottom: 3px solid ${({ $color }) => $color};
  padding-bottom: 2px;
  color: ${({ $color, $hasError }) => ($hasError ? $color : '#fbfcff')};
  text-decoration: ${({ $isOmitted }) => ($isOmitted ? 'line-through' : 'none')};
`;

const SAudioCompareContainer = styled.div`
  background: rgba(122, 67, 136, 0.4);
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 12px;
`;

const SAudioCompareTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(251, 252, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
`;

const SAudioRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  & + & {
    margin-top: 8px;
  }
`;

const SAudioPlayButton = styled.button<{ $playing: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: ${({ $playing }) => ($playing ? 'rgba(255, 255, 255, 0.2)' : '#834581')};
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SAudioLabel = styled.span`
  font-size: 0.9375rem;
  color: #fbfcff;
  font-weight: 500;
`;

const SMetricsRow = styled.div`
  display: flex;
  gap: 6px;
`;

const SMetric = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 6px;
  background: rgba(122, 67, 136, 0.4);
  border-radius: 8px;
`;

const SMetricLabel = styled.div`
  color: rgba(251, 252, 255, 0.7);
  font-size: 0.875rem;
  font-weight: 600;
  margin-top: 4px;
`;
